/**
 * PastWinners — Phase 20.1 (UI overhaul + Wrapped share-card)
 * ------------------------------------------------------------------
 * Hall of Fame styled to match the Ledger (ActivityLog) exactly:
 *   • Wrapper:  border + var(--stakes-border) + var(--stakes-surface)
 *   • Header:   font-anton uppercase title + label-mono count badge
 *   • Rows:     divide-y, flex items-center gap-3 px-4 py-3
 *   • Score:    font-anton text-xl right-aligned (positive cyan)
 *   • Type:     font-mono uppercase across the board
 *
 * Each expanded row exposes a compact terminal-style [ EXPORT ] button
 * that snapshots a hidden 1080×1080 <ShareCard> via html2canvas and
 * downloads it as `stakes-wrapped-<YYYY-MM>.png`. The dossier follows
 * a "MISSION DEBRIEF" aesthetic: pure black, cyan/red accents, stark
 * typography, league-code bottom-stamp.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { api } from "@/lib/api";

const MONTH_LABELS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function formatYM(ym) {
  if (!ym || typeof ym !== "string") return "—";
  const [y, m] = ym.split("-");
  const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1));
  return `${MONTH_LABELS[idx]} ${y}`;
}

// ───────────────────────── Row ─────────────────────────
function Row({ winner, leagueCode, onExport, exporting }) {
  const [open, setOpen] = useState(false);
  const hasWinner = !!winner.winner_id;
  const standings = winner.standings || [];
  const monthLabel = formatYM(winner.year_month);
  const isBusyThis = exporting === winner.year_month;

  return (
    <div data-testid={`past-winner-row-${winner.year_month}`}>
      {/* Collapsed line — same density as a Ledger row */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left flex items-center gap-3 px-4 py-3 font-mono uppercase hover:bg-white/[0.03] transition-colors"
        data-testid={`past-winner-toggle-${winner.year_month}`}
      >
        <div className="label-mono w-20 flex-shrink-0">{monthLabel}</div>
        <div className="flex-1 min-w-0">
          {hasWinner ? (
            <div className="font-barlow text-base uppercase truncate">
              <span className="font-bold">{winner.winner_name || "—"}</span>
              {winner.league_prize ? (
                <span className="label-mono ml-2 text-[var(--stakes-muted)]">
                  🏆 {winner.league_prize}
                </span>
              ) : null}
            </div>
          ) : (
            <span className="label-mono text-[var(--stakes-muted)]">NO POSITIVE SCORER</span>
          )}
        </div>
        <div
          className="font-anton text-xl leading-none flex-shrink-0"
          style={{ color: hasWinner ? "var(--stakes-child)" : "var(--stakes-muted)" }}
        >
          {hasWinner ? `${winner.final_score} pts` : "—"}
        </div>
        <span className="label-mono text-[var(--stakes-muted)] w-3 flex-shrink-0">
          {open ? "▴" : "▾"}
        </span>
      </button>

      {/* Expanded standings — mono, hairline divider, right-aligned scores */}
      {open && (
        <div className="border-t border-[var(--stakes-border)] bg-black/30 px-4 py-3 font-mono uppercase">
          <div className="space-y-1">
            {standings.length === 0 && (
              <div className="label-mono text-[var(--stakes-muted)]">NO STANDINGS RECORDED</div>
            )}
            {standings.map((s, i) => (
              <div
                key={s.user_id || i}
                className="flex items-baseline justify-between gap-3"
                data-testid={`past-winner-${winner.year_month}-standing-${i}`}
              >
                <span className="label-mono w-8 flex-shrink-0">#{i + 1}</span>
                <span className={`flex-1 truncate text-sm ${i === 0 ? "text-[var(--stakes-child)] font-bold" : ""}`}>
                  {s.name || "?"}
                </span>
                <span className="label-mono text-right tabular-nums">{s.score} PTS</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); onExport(winner, leagueCode); }}
              disabled={isBusyThis || !hasWinner}
              className="label-mono px-3 py-1 border border-[var(--stakes-border)] hover:border-[var(--neon-blue)] hover:text-[var(--neon-blue)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid={`past-winner-export-${winner.year_month}`}
            >
              {isBusyThis ? "[ RENDERING… ]" : "[ EXPORT ]"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ───────────────────── Share Card (1080×1080 dossier) ────────────────────
// Rendered off-screen at left:-9999px; html2canvas snapshots it then it's
// torn down. All inline styles (no Tailwind classes) so the snapshot is
// 100% deterministic regardless of viewport / CSS-var inheritance.
const ShareCard = ({ data, leagueCode }) => {
  if (!data) return null;
  const month = formatYM(data.year_month);
  const standings = (data.standings || []).slice(0, 3);
  return (
    <div
      id="stakes-share-card-root"
      style={{
        position: "fixed",
        top: 0,
        left: -9999,
        width: 1080,
        height: 1080,
        background: "#000",
        color: "#fff",
        fontFamily: "'Anton', 'Barlow Condensed', Impact, sans-serif",
        padding: 64,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        // Faint grid texture so the dossier reads as a printout, not a flat poster
        backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
        backgroundSize: "40px 40px",
      }}
    >
      {/* TOP — classification stamp */}
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
            fontSize: 18,
            letterSpacing: 4,
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.55)",
            borderBottom: "1px solid rgba(255,255,255,0.18)",
            paddingBottom: 18,
          }}
        >
          <span>// MISSION DEBRIEF</span>
          <span style={{ color: "#FF3B30" }}>● CLASSIFIED</span>
        </div>
        <div
          style={{
            fontFamily: "ui-monospace, 'JetBrains Mono', monospace",
            fontSize: 16,
            color: "rgba(255,255,255,0.35)",
            letterSpacing: 3,
            marginTop: 16,
            textTransform: "uppercase",
          }}
        >
          DOSSIER / {data.year_month} · LEAGUE {data.league_name || "STAKES"}
        </div>
      </div>

      {/* MIDDLE — winner block */}
      <div style={{ marginTop: 8 }}>
        <div
          style={{
            fontSize: 22,
            letterSpacing: 6,
            color: "rgba(255,255,255,0.55)",
            textTransform: "uppercase",
            fontFamily: "ui-monospace, monospace",
            marginBottom: 12,
          }}
        >
          CHAMPION · {month}
        </div>
        <div
          style={{
            fontSize: 220,
            lineHeight: 0.85,
            textTransform: "uppercase",
            color: "#00D4FF",
            letterSpacing: -4,
            wordBreak: "break-word",
          }}
        >
          {(data.winner_name || "—").toUpperCase()}
        </div>
        <div style={{ marginTop: 36, display: "flex", alignItems: "flex-end", gap: 32 }}>
          <div
            style={{
              border: "3px solid #00D4FF",
              padding: "16px 28px",
              fontSize: 88,
              lineHeight: 1,
              color: "#00D4FF",
            }}
          >
            {data.final_score} PTS
          </div>
          {data.league_prize ? (
            <div
              style={{
                flex: 1,
                fontFamily: "ui-monospace, monospace",
                fontSize: 22,
                textTransform: "uppercase",
                letterSpacing: 2,
                color: "rgba(255,255,255,0.85)",
                lineHeight: 1.3,
                paddingBottom: 8,
              }}
            >
              <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 16, letterSpacing: 4, marginBottom: 6 }}>
                MONTHLY PRIZE
              </div>
              🏆 {data.league_prize}
            </div>
          ) : null}
        </div>
      </div>

      {/* PODIUM — top 3 stripe */}
      <div
        style={{
          display: "flex",
          gap: 16,
          fontFamily: "ui-monospace, monospace",
          textTransform: "uppercase",
          marginTop: 8,
        }}
      >
        {standings.map((s, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              border: `2px solid ${i === 0 ? "#00D4FF" : "rgba(255,255,255,0.2)"}`,
              padding: "20px 18px",
              background: i === 0 ? "rgba(0,212,255,0.05)" : "transparent",
            }}
          >
            <div style={{ fontSize: 18, letterSpacing: 4, color: i === 0 ? "#00D4FF" : "rgba(255,255,255,0.4)", marginBottom: 8 }}>
              #{i + 1}
            </div>
            <div style={{ fontSize: 36, lineHeight: 1, marginBottom: 10, wordBreak: "break-word" }}>
              {(s.name || "?").toUpperCase()}
            </div>
            <div style={{ fontSize: 22, letterSpacing: 2, color: "rgba(255,255,255,0.55)" }}>{s.score} PTS</div>
          </div>
        ))}
      </div>

      {/* BOTTOM — STAKES branding + league code */}
      <div
        style={{
          borderTop: "1px solid rgba(255,255,255,0.18)",
          paddingTop: 22,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          fontFamily: "ui-monospace, monospace",
          textTransform: "uppercase",
        }}
      >
        <div>
          <div style={{ fontSize: 56, lineHeight: 0.9, color: "#fff", letterSpacing: -1 }}>STAKES</div>
          <div style={{ fontSize: 14, letterSpacing: 4, color: "rgba(255,255,255,0.4)", marginTop: 6 }}>
            FAMILY ACCOUNTABILITY · POINTS NEVER LIE
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 14, letterSpacing: 4, color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>
            JOIN THE LEAGUE
          </div>
          <div style={{ fontSize: 48, color: "#FF3B30", letterSpacing: 4 }}>
            #{leagueCode || "------"}
          </div>
        </div>
      </div>
    </div>
  );
};

// ───────────────────── Main panel ─────────────────────
export default function PastWinners({ leagueId, leagueCode }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [exportTarget, setExportTarget] = useState(null);     // winner record currently being rendered
  const [exporting, setExporting] = useState(null);            // year_month currently busy (for button label)
  const shareRootRef = useRef(null);

  useEffect(() => {
    if (!leagueId) return;
    let alive = true;
    api.getPastWinners(leagueId, 24)
      .then((r) => { if (alive) setItems(r.items || []); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || e?.message || "Failed to load history"); });
    return () => { alive = false; };
  }, [leagueId]);

  const handleExport = useCallback(async (winner, code) => {
    setExporting(winner.year_month);
    setExportTarget(winner);
    // wait for the ShareCard to mount + fonts to settle before snapshotting
    await new Promise((r) => setTimeout(r, 120));
    try {
      const node = document.getElementById("stakes-share-card-root");
      if (!node) throw new Error("ShareCard mount missing");
      const canvas = await html2canvas(node, {
        backgroundColor: "#000",
        scale: 1,
        width: 1080,
        height: 1080,
        useCORS: true,
        logging: false,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 0.95));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stakes-wrapped-${winner.year_month}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 500);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[PastWinners] export failed:", e);
    } finally {
      setExporting(null);
      setExportTarget(null);
    }
  }, []);

  return (
    <div
      className="border border-[var(--stakes-border)] bg-[var(--stakes-surface)]"
      data-testid="past-winners-panel"
    >
      {/* Header — mirrors the Ledger */}
      <div className="p-4 md:p-5 border-b border-[var(--stakes-border)] flex items-center justify-between">
        <div>
          <div className="label-mono">MONTHLY WINNERS</div>
          <h3 className="font-anton text-2xl uppercase leading-none mt-1 tracking-tight">HALL OF FAME</h3>
        </div>
        <div className="label-mono">
          {items === null ? "" : items.length === 0 ? "0 ENTRIES" : `${items.length} ENTR${items.length === 1 ? "Y" : "IES"}`}
        </div>
      </div>

      {error && (
        <div className="p-6 label-mono text-center" style={{ color: "var(--stakes-primary)" }} data-testid="past-winners-error">
          {error.toUpperCase()}
        </div>
      )}
      {!error && items === null && (
        <div className="p-6 label-mono text-center" data-testid="past-winners-loading">LOADING…</div>
      )}
      {!error && items && items.length === 0 && (
        <div className="p-6 label-mono text-center" data-testid="past-winners-empty">
          NO MONTHS SETTLED YET — CHECK BACK ON THE 1ST
        </div>
      )}
      {!error && items && items.length > 0 && (
        <div className="max-h-80 overflow-y-auto divide-y divide-[var(--stakes-border)]">
          {items.map((w) => (
            <Row
              key={w.id || w.year_month}
              winner={w}
              leagueCode={leagueCode}
              onExport={handleExport}
              exporting={exporting}
            />
          ))}
        </div>
      )}

      {/* Hidden off-screen share card — only mounted while exporting */}
      <div ref={shareRootRef}>
        {exportTarget && <ShareCard data={exportTarget} leagueCode={leagueCode} />}
      </div>
    </div>
  );
}
